import clsx from 'clsx';
import { forwardRef, type ReactElement } from 'react';
import { useIntl } from 'react-intl';

import type { DataTestId } from '../../../core/types/data-props.js';
import type { StylingProps } from '../../../core/types/styling-props.js';
import type { WithChildren } from '../../../core/types/with-children.js';
import { Text } from '../../../typography/text/Text.js';
import { useDataTable } from '../contexts/DataTableContext.js';
import { dataTableMessages } from '../messages.js';
import * as styles from './DataTableEmptyState.sty.js';

type EmptyStateProps = WithChildren & StylingProps & DataTestId;

// A helper function to check whether the child component is the EmptyState component
export function _isEmptyState(
  child: ReactElement | null,
): child is ReturnType<typeof DataTableEmptyState> {
  return child?.type === DataTableEmptyState;
}

/**
 * EmptyState component used to indicate the empty state to the end user.
 * @public
 */
export const DataTableEmptyState = /* @__PURE__ */ forwardRef<
  HTMLDivElement,
  EmptyStateProps
>(
  (
    {
      children,
      className: consumerClassName,
      style: consumerStyle,
      'data-testid': dataTestId,
      ...remainingProps
    },
    forwardedRef,
  ): ReactElement | null => {
    const intl = useIntl();
    const table = useDataTable();

    // If the slot contains custom content that is not a string, we can render it directly
    if (children && typeof children !== 'string') {
      return (
        <div
          ref={forwardedRef}
          className={clsx(styles.stickyContainer, consumerClassName)}
          style={consumerStyle}
          data-testid={dataTestId}
          {...remainingProps}
        >
          {children}
        </div>
      );
    }

    const isColumnsEmpty =
      table.getVisibleLeafColumnsWithoutBuiltin().length === 0;
    const isDataEmpty = table.getRowModel().rows.length === 0;
    const rowDensity = table.atoms.visualVariantConfig?.get().rowDensity;
    const fallbackMessage = isDataEmpty
      ? intl.formatMessage(dataTableMessages.noDataAvailable)
      : intl.formatMessage(dataTableMessages.noColumnsAvailable);

    return (
      <Text
        ref={forwardedRef}
        className={clsx([
          styles.stickyContainer,
          styles.emptyState({ minWidth: isColumnsEmpty, rowDensity }),
          consumerClassName,
        ])}
        style={consumerStyle}
        data-testid={dataTestId}
        {...remainingProps}
      >
        {/* Empty string should also fall back to the default message */}
        {children || fallbackMessage}
      </Text>
    );
  },
);

(
  DataTableEmptyState as typeof DataTableEmptyState & {
    displayName: string;
  }
).displayName = 'EmptyState';
